import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchUserLoans } from '../utils/loanService';
import Navbar from '../components/Navbar';

const fmt = n => `Ksh ${Number(n).toLocaleString()}`;

export default function LoanHistory() {
  const { currentUser } = useAuth();
  const navigate        = useNavigate();
  const [loans,   setLoans]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;
    fetchUserLoans(currentUser.uid).then(data => {
      setLoans(data);
      setLoading(false);
    });
  }, [currentUser]);

  // Only show loans where Payhero confirmed payment (feePaid === true)
  const paidLoans = loans.filter(l => l.feePaid === true);

  return (
    <>
      <Navbar />
      <div className="page-shell">
        <div className="page-inner">
          <div className="page-eyebrow">📜 Your History</div>
          <h1 className="page-title">Loan History</h1>
          <p className="page-sub">Track your applications and disbursements</p>

          {loading ? (
            <div style={{ textAlign:'center', padding:'60px 20px' }}>
              <div className="loading-spinner"></div>
              <p style={{ color:'var(--text-muted)', fontSize:14, marginTop:8 }}>Loading…</p>
            </div>

          ) : paidLoans.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3 className="empty-title">No loans yet</h3>
              <p className="empty-sub">
                Apply now and get funds in your M-Pesa within 24 hours.
              </p>
              <button className="btn-cta"
                style={{ maxWidth:220, margin:'0 auto', display:'flex' }}
                onClick={() => navigate('/dashboard')}>
                Apply for a Loan →
              </button>
            </div>

          ) : (
            <div>
              {paidLoans.map((loan, i) => {
                const isPending   = loan.status === 'pending' || !loan.status;
                const isDisbursed = loan.status === 'disbursed';
                const date = loan.createdAt
                  ? new Date(loan.createdAt).toLocaleDateString('en-KE', { dateStyle:'medium' })
                  : '—';
                const key = `${loan.reference || loan.id || 'loan'}-${i}`;

                return (
                  <div key={key} style={{
                    background: '#fff',
                    border: `1.5px solid ${isPending ? 'var(--green-200)' : 'var(--border)'}`,
                    borderRadius: 'var(--r-lg)',
                    padding: '18px 20px',
                    marginBottom: 12,
                    boxShadow: isPending ? '0 2px 12px rgba(5,150,105,0.10)' : 'var(--shadow-xs)',
                  }}>

                    {/* Amount + status */}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:10 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontFamily:'DM Serif Display, serif', fontSize:22, color:'var(--primary)', marginBottom:4, letterSpacing:'-0.2px' }}>
                          {fmt(loan.loanAmount)}
                        </div>
                        <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:2 }}>
                          {loan.loanType || 'Personal Loan'} · Fee: Ksh {Number(loan.loanFee||0).toLocaleString()}
                        </div>
                        <div style={{ fontSize:12, color:'var(--text-light)' }}>
                          Applied: {date}
                        </div>
                      </div>

                      {/* Status badge */}
                      <div style={{
                        display:'inline-flex', alignItems:'center', gap:5,
                        padding:'5px 12px', borderRadius:100,
                        fontSize:12, fontWeight:700, whiteSpace:'nowrap',
                        ...(isPending
                          ? { background:'#fffbeb', color:'#92400e', border:'1px solid #fde68a' }
                          : isDisbursed
                          ? { background:'#eff6ff', color:'#1e40af', border:'1px solid #bfdbfe' }
                          : { background:'#f0fdf4', color:'#166534', border:'1px solid #bbf7d0' })
                      }}>
                        {isPending ? '⏳ Processing' : isDisbursed ? '💰 Disbursed' : '✅ Approved'}
                      </div>
                    </div>

                    {/* Reference + expected */}
                    <div style={{
                      background:'var(--bg)', borderRadius:'var(--r-sm)',
                      padding:'8px 12px',
                      fontSize:12, color:'var(--text-muted)',
                      display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:4,
                    }}>
                      <span>Ref: <strong style={{ color:'var(--text)', fontSize:11 }}>{loan.reference || '—'}</strong></span>
                      {loan.dueDate && (
                        <span>Expected: <strong style={{ color:'var(--text)' }}>{loan.dueDate}</strong></span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Apply again */}
              <div
                style={{
                  background:'var(--green-50)', border:'1.5px dashed var(--green-200)',
                  borderRadius:'var(--r-lg)', padding:'20px', textAlign:'center',
                  marginBottom:16, cursor:'pointer',
                }}
                onClick={() => navigate('/dashboard')}>
                <div style={{ fontSize:26, marginBottom:8 }}>➕</div>
                <div style={{ fontWeight:700, color:'var(--primary)', fontSize:15, marginBottom:4 }}>
                  Apply for Another Loan
                </div>
                <div style={{ fontSize:13, color:'var(--text-muted)', fontWeight:300 }}>
                  Need more funds? Apply anytime — first come, first served
                </div>
              </div>
            </div>
          )}

          <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </>
  );
}
